import {computed, inject, Injectable, signal} from '@angular/core';
import {RxStompService} from '@stomp/ng2-stompjs';
import {IUserAuthResponse} from '../models/i-user-auth-response';
import {ILoginResponse} from '../models/i-login-response';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Router} from '@angular/router';
import {ITaxInformationResponse} from '../../features/profile/billing/models/i-tax-information-response';
import {Role} from '../enums/role';
import {EmployeeRole} from '../enums/employee-role';
import {Currency} from '../../shared/enums/currency';
import {environment} from '../../../environments/environment.local';
import {ISubscriptionPlanResponse} from '../../shared/models/i-subscription-plan-response';
import {NotificationService} from '../../shared/services/notification-service';

@Injectable({
  providedIn: 'root',
})
export class ProfileSync {
  rxStomp: RxStompService = inject(RxStompService);
  jwtHelper: JwtHelperService = new JwtHelperService();
  router: Router = inject(Router);
  notificationService: NotificationService = inject(NotificationService);

  private placeholderUser: ILoginResponse = {
    jwt: '',
    user: {
      id: 0,
      username: "",
      email: "",
      name: "",
      surname: "",
      photoUrl: "",
      role: Role.USER,
      representedEmployee: {
        name: "",
        surname: "",
        role: EmployeeRole.CONSULTANT,
        createdBy: {
          username: "",
          photoUrl: ""
        }
      },
      settings: {
        newSaleNotification: false,
        newCustomerNotification: false,
        birthdayNotification: false,
        expirationNotification: false,
        allowCustomerCreation: false,
        allowSaleCreation: false,
        currency: Currency.EUR
      },
      taxInformation: null
    },
    notificationBadges: {
      hasRecalls: false,
      hasBirthdays: false
    }
  }

  private _user = signal<ILoginResponse | null>(environment.app ? this.placeholderUser : this.restoreUser());
  user = this._user.asReadonly();
  isLogged = computed(() => !!this._user());
  isAdmin = computed(() => this.user()?.user.role === Role.ADMIN);
  isBasic = computed(() => this.user()?.user.plan?.name.toLowerCase() == 'basic');
  isPro = computed(() => this.user()?.user.plan?.name.toLowerCase() == 'pro');

  constructor() {
    if (environment.app) {
      this.notificationService.setBadges(this.placeholderUser.notificationBadges);
    }
  }

  loginFromResponse(dto: ILoginResponse): void {
    localStorage.setItem('accessData', JSON.stringify(dto));
    this.setUser(dto);
    this.notificationService.setBadges(dto.notificationBadges);
    this.autoLogout(dto.jwt);
  }

  logout(): void {
    this.disconnect();
    this.setUser(null);
    this.notificationService.clearBadges();
    localStorage.removeItem('accessData');
    this.router.navigate(['']);
  }

  autoLogout(jwt:string):void{
    const expDate:Date = this.jwtHelper.getTokenExpirationDate(jwt) as Date;
    const expMs:number = expDate.getTime() - new Date().getTime();

    setTimeout(() => {
      this.logout();
    }, expMs);
  }

  restoreUser(): ILoginResponse | null {
    const userJson: string | null = localStorage.getItem("accessData");
    if (!userJson) return null;

    const accessData: ILoginResponse = JSON.parse(userJson);
    if (this.jwtHelper.isTokenExpired(accessData.jwt)) {
      localStorage.removeItem('accessData');
      return null;
    }

    this.notificationService.setBadges(accessData.notificationBadges);
    this.autoLogout(accessData.jwt);
    this.connect(accessData.jwt);
    return accessData;
  }

  updateUser(user: IUserAuthResponse): void {
    const accessData: ILoginResponse = this.user()!;
    accessData.user = user;
    localStorage.setItem('accessData', JSON.stringify(accessData));
    this._user.set(accessData);
  }

  updatePlan(plan: ISubscriptionPlanResponse | null): void {
    if (!plan) return;

    const accessData: ILoginResponse = this.user()!;
    accessData.user.plan = plan;
    localStorage.setItem('accessData', JSON.stringify(accessData));
    this._user.set(accessData);
  }

  updateTaxInformation(taxInformation: ITaxInformationResponse): void {
    const accessData: ILoginResponse = this.user()!;
    accessData.user.taxInformation = taxInformation;
    localStorage.setItem('accessData', JSON.stringify(accessData));
    this._user.set(accessData);
    console.log(accessData)
    console.log(this._user())
  }

  updateRecallsBadge(hasRecalls: boolean): void {
    const accessData = this.user();
    if (accessData) {
      accessData.notificationBadges.hasRecalls = hasRecalls;
      localStorage.setItem('accessData', JSON.stringify(accessData));
      this._user.set(accessData);
    }
    this.notificationService.setHasRecalls(hasRecalls);
  }

  setUser(user: ILoginResponse | null): void {
    this._user.set(user);
  }

  connect(jwt: string): void {
    if (environment.app) return;
    this.disconnect();

    // Sovrascriviamo la configurazione con il token aggiornato
    this.rxStomp.configure({
      connectHeaders: {
        Authorization: `Bearer ${jwt}`
      }
    });

    this.rxStomp.activate();

    this.rxStomp.watch('/user/queue/profile').subscribe({
      next: (message) => {
        const updatedUser: IUserAuthResponse = JSON.parse(message.body);
        this.updateUser(updatedUser);
      },
      error: (err) => console.error('Errore WebSocket:', err)
    });
  }

  disconnect(): void {
    if (this.rxStomp.active) {
      this.rxStomp.deactivate();
      console.log('🔌 WebSocket disconnesso correttamente.');
    }
  }
}
